/**
 * Quién puede entrar a qué módulo.
 *
 * 🔴 POR QUÉ EXISTE ESTE ARCHIVO (medido el 2026-09-04). La comparación vivía suelta, en
 *    dos lugares, y distinguía mayúsculas: `App.tsx` preguntaba por `'proyectos'` y la base
 *    contesta `'Proyectos'`. Resultado: TODO usuario que no fuera N4 quedaba fuera de casi
 *    todo el ERP —Proyectos, Egresos, Ingresos, Bancos, Contratos, Créditos, Reportes,
 *    Ventas, Post-venta— mientras el backend lo dejaba pasar sin problema.
 *
 *    Se comprobó ABRIENDO la pantalla con el CFO: el menú enseñaba sólo Dashboard, Tablero,
 *    Obra y Notificaciones, que son exactamente los cuatro módulos que en la base están
 *    escritos en minúscula.
 *
 * 🟢 La regla vive AQUÍ, una sola vez, y la usan la ruta y el menú. Cuando estaba escrita
 *    dos veces, arreglar una habría dejado la otra mintiendo: el menú sin la opción, o la
 *    opción llevando a una pantalla que rebota.
 *
 * ⚠️ NO se "arregla" normalizando la base: los nombres vienen de `usuarios_modulos`, que es
 *    dato del cliente y cada tenant puede escribirlos como quiera. El código es el que
 *    tiene que ser tolerante, no el dato el que tiene que ser perfecto.
 */

import type { AccesoDeRol, LecturasPermitidas, ModuloDeDinero } from '@erp/shared';

/** Un nombre de módulo, comparable: sin mayúsculas y sin espacios de sobra. */
export const normalizar = (m: string) => m.trim().toLowerCase();

/** Los módulos de dinero: su puerta la manda el SERVIDOR (`accesos`), no `usuarios_modulos` (por semilla todos tienen todo). */
const MODULOS_DE_DINERO: readonly ModuloDeDinero[] = ['ingresos', 'egresos', 'bancos', 'creditos', 'contabilidad'];
const esModuloDeDinero = (m: string): m is ModuloDeDinero => (MODULOS_DE_DINERO as readonly string[]).includes(normalizar(m));

/**
 * ¿Este usuario puede entrar a este módulo?
 *
 * 🔒 I-230 (D-2026-09-29-G/J): para los módulos de DINERO manda `accesos` (la tabla del servidor, por rol). Si `accesos`
 * no viene, se CIERRA — un front viejo o una sesión a medias no abre bancos por omisión. Para el resto, igual que antes.
 *
 * @param modulosDelUsuario  tal como vienen del servidor, sin tocar
 * @param modulo             el id que usa la ruta o el menú
 * @param nivel              N4 ve todo (salvo dinero: ahí manda la tabla, y el CEO la trae toda en ✅)
 * @param accesos            `usuario.accesos` de `/auth/me`
 */
export function tieneAccesoAModulo(modulosDelUsuario: string[], modulo: string, nivel: number, accesos?: AccesoDeRol): boolean {
  // 🔒 I-257 (D-2026-10-02-A/B): el OVERHEAD no es un módulo de dinero de la tabla, pero lo manda la misma celda —`corporativas`—
  // que manda el servidor (`verificarCorporativas`). Sin `accesos`, se cierra: la pantalla refleja, el servidor decide.
  // D-2026-10-22-M: `corporativas` ya trae la casilla aplicada (y la excepción del CFO): la pantalla no la vuelve a mirar.
  if (normalizar(modulo) === 'overhead') return accesos?.corporativas === true;
  if (esModuloDeDinero(modulo)) {
    if (accesos?.[normalizar(modulo) as ModuloDeDinero] !== true) return false;
  }
  if (nivel >= 4) return true;
  // Lista vacía = no hay restricción configurada. Se conserva el comportamiento de antes:
  // un usuario sin módulos asignados no se queda encerrado fuera del sistema entero.
  if (modulosDelUsuario.length === 0) return true;
  const objetivo = normalizar(modulo);
  return modulosDelUsuario.some(m => normalizar(m) === objetivo);
}

/** D-2026-10-22-M — ¿la pantalla ofrece CAPTURAR en la oficina? Lo decide el servidor (`accesos.operaCorporativas`: el CFO VE sin la casilla, no opera); si falta, no. */
export const operaLaOficina = (accesos?: AccesoDeRol): boolean => accesos?.operaCorporativas === true;
/** Y si no la opera, por qué: el texto lo manda el servidor (`accesos.razonSinOficina`, el mismo del 403). Si no llegó, se dice eso — no se inventa la causa. */
export const razonSinOficina = (accesos?: AccesoDeRol): string => accesos?.razonSinOficina ?? 'Tus permisos no terminaron de cargar: recarga la página para operar lo de la oficina.';

/** Los ids del menú que este usuario sí puede ver. Misma regla que la ruta, a propósito. */
export function modulosVisibles(idsDelMenu: string[], modulosDelUsuario: string[], nivel: number, accesos?: AccesoDeRol): string[] {
  return idsDelMenu.filter(id => tieneAccesoAModulo(modulosDelUsuario, id, nivel, accesos));
}

/**
 * BUG-4061 · BUG-4064 — ¿qué LECTURAS puede pedir la casa para este usuario? Las calcula el SERVIDOR con los candados de cada ruta
 * (`lecturasPermitidasDe`, backend/src/middleware/modulo.middleware.ts) y las manda `/auth/me` en `lecturas`; aquí sólo se leen.
 * Sirve para NO PEDIR lo que el rol no ve (antes un vendedor disparaba 6 respuestas 403 en Unidades, 12 en Ingresos y 12 en Pagos).
 * Si falta `lecturas` (respuesta vieja de `/auth/me`), no se pide ninguna: se cierra, no se abre.
 */
const NINGUNA: LecturasPermitidas = {
  resumenFinanciero: false, presupuesto: false, cobranza: false, cuentas: false, creditos: false, alertas: false, ritmoVenta: false, catalogosDeEgresos: false, recurrentesDeEgresos: false,
};
export function lecturasPermitidas(u: { lecturas?: LecturasPermitidas } | null | undefined): LecturasPermitidas {
  return u?.lecturas ?? NINGUNA;
}
